import { useRef } from "react";
import {
  Check, Loader2, AlertTriangle, ChevronLeft, ChevronRight, Eye, EyeOff,
} from "lucide-react";
import { STEP_STATUS } from "@/data/mockData";

const nodeStyle = (status) => {
  if (status === STEP_STATUS.DONE)
    return {
      ring: "bg-[#111111] text-white",
      Icon: Check,
      dot: "#111111",
      line: "bg-[#111111]",
    };
  if (status === STEP_STATUS.IN_PROGRESS)
    return {
      ring: "bg-[#C8FF2F] text-[#111111]",
      Icon: Loader2,
      dot: "#111111",
      line: "bg-[#EEF2F5]",
      pulse: true,
    };
  if (status === STEP_STATUS.BLOCKED)
    return {
      ring: "bg-[#FEE2E2] text-[#991B1B] border border-[#EF4444]/30",
      Icon: AlertTriangle,
      dot: "#EF4444",
      line: "bg-[#EEF2F5]",
    };
  return {
    ring: "bg-white text-[#9CA3AF] border border-[#EEF2F5]",
    Icon: () => (
      <span className="h-2 w-2 rounded-full bg-[#D1D5DB] inline-block" />
    ),
    dot: "#D1D5DB",
    line: "bg-[#EEF2F5]",
  };
};

const statusLabel = (status) => {
  if (status === STEP_STATUS.DONE) return "Terminé";
  if (status === STEP_STATUS.IN_PROGRESS) return "En cours";
  if (status === STEP_STATUS.BLOCKED) return "Bloqué";
  return "À venir";
};

const WorkflowTimeline = ({ steps = [], workflowSummary, onSelect, activeKey }) => {
  const scrollerRef = useRef(null);

  const scroll = (dir) => {
    scrollerRef.current?.scrollBy({
      left: dir === "left" ? -320 : 320,
      behavior: "smooth",
    });
  };

  const totals = workflowSummary?.totals;
  const active = workflowSummary?.active_step;
  const next = workflowSummary?.next_action;

  return (
    <section
      className="wyvio-card p-6 lg:p-8"
      data-testid="workflow-timeline"
    >
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-[10.5px] font-semibold text-[#22C55E] uppercase tracking-wider">
            Workflow
          </p>
          <h3 className="mt-1 text-[16px] font-semibold text-[#111111] tracking-tight">
            Parcours du véhicule
          </h3>
          {totals && (
            <p className="mt-1 text-[12.5px] text-[#6B7280]">
              <span className="font-semibold text-[#111111]">
                {totals.completed}/{totals.total}
              </span>{" "}
              étapes terminées
              {totals.blocked > 0 && (
                <span className="text-[#991B1B] font-medium">
                  {" · "}
                  {totals.blocked} bloquée{totals.blocked > 1 ? "s" : ""}
                </span>
              )}
            </p>
          )}
        </div>

        {(active || next) && (
          <div
            data-testid="workflow-next-action"
            className="rounded-2xl bg-[#F7F8FA] border border-[#EEF2F5] px-4 py-2.5 max-w-[280px]"
          >
            <p className="text-[10.5px] font-semibold text-[#6B7280] uppercase tracking-wider">
              Prochaine action
            </p>
            <p className="mt-0.5 text-[13.5px] font-semibold text-[#111111] leading-tight">
              {active?.label || next?.label || "—"}
            </p>
          </div>
        )}

        <div className="hidden sm:flex items-center gap-1.5">
          <button
            onClick={() => scroll("left")}
            data-testid="timeline-scroll-left"
            aria-label="Précédent"
            className="h-8 w-8 rounded-full border border-[#EEF2F5] bg-white flex items-center justify-center text-[#111111] hover:bg-[#F7F8FA] transition-colors"
          >
            <ChevronLeft size={14} strokeWidth={2} />
          </button>
          <button
            onClick={() => scroll("right")}
            data-testid="timeline-scroll-right"
            aria-label="Suivant"
            className="h-8 w-8 rounded-full border border-[#EEF2F5] bg-white flex items-center justify-center text-[#111111] hover:bg-[#F7F8FA] transition-colors"
          >
            <ChevronRight size={14} strokeWidth={2} />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="relative overflow-x-auto scrollbar-thin pb-2"
        style={{ scrollbarWidth: "none" }}
      >
        <ol className="flex items-start gap-0 min-w-full">
          {steps.map((step, idx) => {
            const s = nodeStyle(step.status);
            const isLast = idx === steps.length - 1;
            const isActive = activeKey === step.key;
            const Icon = s.Icon;

            return (
              <li
                key={step.key}
                className="flex-1 min-w-[130px] flex flex-col items-center relative"
              >
                {!isLast && (
                  <span
                    aria-hidden
                    className={`absolute top-[19px] left-[calc(50%+22px)] right-[calc(-50%+22px)] h-px ${s.line}`}
                  />
                )}

                <button
                  onClick={() => onSelect?.(step)}
                  data-testid={`timeline-step-${step.key}`}
                  className={[
                    "relative z-10 h-10 w-10 rounded-full flex items-center justify-center transition-all duration-200",
                    s.ring,
                    isActive
                      ? "ring-4 ring-[#111111]/10 scale-105"
                      : "hover:scale-105",
                  ].join(" ")}
                >
                  <Icon size={16} strokeWidth={2.25} className={s.pulse ? "animate-spin-slow" : ""} />
                </button>

                <div className="mt-3 text-center px-1 max-w-[130px]">
                  <p
                    className={[
                      "text-[12px] font-medium leading-tight tracking-tight",
                      step.status === STEP_STATUS.UPCOMING
                        ? "text-[#9CA3AF]"
                        : "text-[#111111]",
                    ].join(" ")}
                  >
                    {step.label || step.title}
                  </p>
                  <div className="mt-1 flex items-center justify-center gap-1">
                    <p
                      className="text-[10.5px] font-medium"
                      style={{ color: s.dot }}
                    >
                      {statusLabel(step.status)}
                    </p>
                    {step.visible_client === false && (
                      <span
                        title="Interne — non visible côté client"
                        className="text-[#9CA3AF]"
                      >
                        <EyeOff size={9} strokeWidth={2.25} />
                      </span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

export default WorkflowTimeline;
